import { Box, Text, Tooltip, UnstyledButton } from '@mantine/core';
import {
	NavigationIntent,
	OrganizationType,
	routingClient
} from '@newo/routing-client';
import { Pencil } from 'lucide-react';
import { type FC, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useHostContext } from '@/context/HostContext.tsx';
import useAppStore from '@/store/app';
import {
	STEPPER_AGENT_AVATAR_URL,
	STEPPER_AGENT_NAME_ATTRIBUTE_IDN,
	STEPPER_PORTAL_SETTINGS_ATTRIBUTE_QUERY_PARAM
} from '../../constants';
import { useStepperOnboarding } from '../../context/StepperOnboardingContext';
import { resetStepperRuntime } from '../../resetStepperRuntime';
import type { StepperOnboardingFormValues } from '../../types';
import { formatStepperDisplayPhoneNumber } from '../../utils';
import { CLASS_NAMES } from './classNames';

const StepperAgentProfileCard: FC = () => {
	const { localization } = useHostContext();
	const { control, getValues } = useFormContext<StepperOnboardingFormValues>();
	const { isAgentReady, isSetupActivationVisible } = useStepperOnboarding();
	const agentName = useWatch({
		control,
		name: 'agentName'
	});
	const agentPhoneNumber = useWatch({
		control,
		name: 'agentPhoneNumber'
	});
	const displayedAgentName =
		agentName?.trim() ||
		localization.translate('creator', 'stepperOnboarding.profile.agentName');
	const displayedAgentPhoneNumber =
		formatStepperDisplayPhoneNumber(agentPhoneNumber);
	const disabledActionTooltipLabel = localization.translate(
		'creator',
		'stepperOnboarding.profile.disabledActionTooltip'
	);
	const disabledActionTooltipContent = (
		<span data-testid="stepper-agent-disabled-action-tooltip">
			{disabledActionTooltipLabel}
		</span>
	);
	const handleNavigateToSettings = useCallback(() => {
		const createdCustomerIdn =
			getValues('createdCustomerIdn') || useAppStore.getState().customerIdn;
		if (!createdCustomerIdn) {
			return;
		}

		resetStepperRuntime();
		routingClient.emitNavigation({
			intent: NavigationIntent.CustomerSettings,
			params: {
				organizationIdn: createdCustomerIdn,
				organizationType: OrganizationType.Customer
			},
			query: {
				[STEPPER_PORTAL_SETTINGS_ATTRIBUTE_QUERY_PARAM]:
					STEPPER_AGENT_NAME_ATTRIBUTE_IDN
			}
		});
	}, [getValues]);

	return (
		<Box
			className={CLASS_NAMES.agentCard}
			data-testid="stepper-agent-profile-card"
		>
			<Box className="flex min-w-0 items-center gap-3">
				<Box
					aria-hidden="true"
					className={CLASS_NAMES.agentAvatar}
				>
					<img
						alt={localization.translate(
							'creator',
							'stepperOnboarding.profile.agentAvatarAlt'
						)}
						className="mt-0 h-16 w-16"
						src={STEPPER_AGENT_AVATAR_URL}
					/>
				</Box>
				<Box className="min-w-0">
					<Box className={CLASS_NAMES.agentNameRow}>
						<Text
							className="truncate text-xl font-bold text-[var(--color-primary-900)]"
							component="span"
						>
							{displayedAgentName}
						</Text>
						<Tooltip
							disabled={isSetupActivationVisible}
							label={disabledActionTooltipContent}
							transitionProps={{ duration: 0 }}
							withArrow
						>
							<UnstyledButton
								aria-label={localization.translate(
									'creator',
									'stepperOnboarding.profile.editAgentName'
								)}
								className="inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors"
								data-testid="stepper-agent-name-edit-button"
								disabled={!isSetupActivationVisible}
								onClick={handleNavigateToSettings}
								type="button"
							>
								<Pencil
									aria-hidden="true"
									className="h-3 w-3 text-[var(--color-primary-900)]"
								/>
							</UnstyledButton>
						</Tooltip>
					</Box>

					<Text className={CLASS_NAMES.profileMeta}>
						{localization.translate(
							'creator',
							'stepperOnboarding.profile.phoneTitle'
						)}{' '}
						{isAgentReady || isSetupActivationVisible
							? displayedAgentPhoneNumber
							: localization.translate(
									'creator',
									'stepperOnboarding.profile.phonePending'
								)}
					</Text>
				</Box>
			</Box>
		</Box>
	);
};

export default StepperAgentProfileCard;
